/**
 * LiveStatusLine — the persistent "what am I doing right now" row.
 *
 * Pulse dot + cross-fading sentence ("Reading wiki page 3/12…"). Rendered
 * in two places:
 *   1. inside TypingIndicator (empty assistant bubble, pre-first-token);
 *   2. BELOW streamed markdown in MessageItem while a later tool round runs
 *      (the slot that survives token arrival).
 *
 * RTL-safe: dir="auto" + logical CSS in TypingIndicator.css. Honors
 * prefers-reduced-motion via the existing CSS.
 */

import { memo, useEffect, useRef, useState } from 'react';
import { LIVE_COUNT_MARKER } from '../utils/progressStatus';
import './TypingIndicator.css';

interface LiveStatusLineProps {
  /** The resolved status sentence. Cross-fades (key swap) when it changes. */
  text: string;
  /** Epoch ms when this status was first shown. Kept for callers that key
   *  their status on it; nothing is displayed from it any more — the seconds
   *  it produced did not match what the user experienced (operator request
   *  2026-09-10), so the line shows only what the agent is doing. */
  since?: number;
  /** Extra class for placement variants (e.g. stacked under streamed text). */
  className?: string;
}


/** 1234 → "1.2K"; below a thousand the plain number. */
function formatCount(n: number): string {
  if (n < 1000) return String(n);
  const k = n / 1000;
  return `${k >= 10 ? Math.round(k) : k.toFixed(1)}K`;
}

/**
 * A number that climbs to its target one step at a time instead of jumping
 * — the backend reports a new target every ~2s and this spreads the climb
 * across the frames in between (operator request 2026-09-10).
 */
function useClimbingCount(target: number | null, sentence: string): number {
  const [shown, setShown] = useState(0);
  const shownRef = useRef(0);
  const sentenceRef = useRef(sentence);
  useEffect(() => {
    if (target === null) {
      shownRef.current = 0;
      setShown(0);
      return;
    }
    // A different counting sentence is a different count ("… words of
    // reasoning" → "Writing the changes… N words"): start it from zero,
    // or the old, higher number would stay on screen.
    if (sentenceRef.current !== sentence) {
      sentenceRef.current = sentence;
      shownRef.current = 0;
      setShown(0);
    }
    let frame = 0;
    let last = performance.now();
    const step = (now: number) => {
      const current = shownRef.current;
      if (current >= target) return;
      // Cover the remaining distance in ~1.2s at 60fps, never slower than
      // one per frame, never faster than the gap allows — small gaps tick
      // 1, 2, 3…; a 200-word gap climbs in steps of three.
      const dt = Math.max(1, now - last);
      last = now;
      const perMs = (target - current) / 1200;
      const next = Math.min(target, current + Math.max(1, Math.round(perMs * dt)));
      shownRef.current = next;
      setShown(next);
      if (next < target) frame = window.requestAnimationFrame(step);
    };
    frame = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(frame);
  }, [target, sentence]);
  return shown;
}

export const LiveStatusLine = memo(function LiveStatusLine({
  text,
  className,
}: LiveStatusLineProps) {
  // A sentence carrying a live count: animate the number.
  const countMatch = text.match(LIVE_COUNT_MARKER);
  const target = countMatch ? parseInt(countMatch[1], 10) : null;
  const stableText = countMatch ? text.replace(LIVE_COUNT_MARKER, '') : text;
  const shownCount = useClimbingCount(target, stableText);
  // Never show "0 words": the first frame renders before the climb starts.
  const visibleCount = target !== null ? Math.max(Math.min(1, target), shownCount) : 0;
  const display = countMatch ? text.replace(LIVE_COUNT_MARKER, formatCount(visibleCount)) : text;

  return (
    <div
      className={`thinking-indicator-content${className ? ` ${className}` : ''}`}
      dir="auto"
      aria-live="polite"
      aria-atomic="true"
    >
      <span key={stableText} className="thinking-pulse-dot" aria-hidden="true" />
      <span dir="auto" key={`label-${stableText}`} className="thinking-label">
        {display}
      </span>
    </div>
  );
});
